import { RARITY_LABELS, type CollectionConfig, type SizeClass, type WeightedOption } from "../core/types";

const SIZE_CLASSES: readonly SizeClass[] = ["small", "medium", "large", "colossal"];

export function validateCollectionConfig(config: CollectionConfig): CollectionConfig {
  if (!config.id.trim()) throw new Error("Collection protocol ID is required.");
  if (!config.publicId.trim()) throw new Error("Public collection ID is required.");
  if (!config.name.trim()) throw new Error("Collection name is required.");
  if (!/^\d+\.\d+\.\d+$/.test(config.version)) throw new Error("Collection version must use semantic x.y.z format.");
  if (!Number.isInteger(config.itemCount) || config.itemCount <= 0) {
    throw new Error("Collection item count must be a positive integer.");
  }

  validateWeights("size", config.sizeWeights);
  validateWeights("form", config.formFamilies);
  validateWeights("finish", config.finishFamilies);
  validateWeights("pattern", config.patternFamilies);
  validateWeights("palette", config.paletteFamilies);
  validateDefinitions("finish", config.finishFamilies, config.finishDefinitions);
  validateDefinitions("pattern", config.patternFamilies, config.patternDefinitions);
  validateDefinitions("palette", config.paletteFamilies, config.paletteDefinitions);
  if (!Number.isFinite(config.ventralTreatment.foldEmphasis) || config.ventralTreatment.foldEmphasis <= 0) {
    throw new Error("Ventral fold emphasis must be finite and positive.");
  }
  const lathe = config.lathePropulsion;
  if (lathe.axisAlignment !== "ventral-fold" && lathe.axisAlignment !== "shell-endpoints") {
    throw new Error("Collection lathe axis alignment is invalid.");
  }
  for (const [name, value] of Object.entries(lathe)) {
    if (name !== "axisAlignment" && !Number.isFinite(value)) {
      throw new Error(`Collection lathe parameter ${name} must be finite.`);
    }
  }

  for (const size of SIZE_CLASSES) {
    if (!(config.sizeScale[size] > 0)) throw new Error(`Size scale must be positive for ${size}.`);
    const rarity = config.rarityForSize[size];
    if (!rarity) throw new Error(`Rarity mapping is missing for ${size}.`);
    if (!RARITY_LABELS.includes(rarity)) throw new Error(`Unsupported rarity label for ${size}: ${rarity}`);
  }
  for (const [name, range] of Object.entries(config.geometryConstraints)) {
    if (!Number.isFinite(range.min) || !Number.isFinite(range.max) || range.max < range.min) {
      throw new Error(`Geometry constraint ${name} has an invalid range.`);
    }
  }
  validateResolution("production", config.textureResolution);
  validateResolution("review", config.reviewTextureResolution);
  if (config.defaultAssetFormats.length === 0) throw new Error("At least one default asset format is required.");
  if (!config.naming.itemPrefix.trim()) throw new Error("Item filename prefix is required.");
  if (!Number.isInteger(config.naming.tokenPadding) || config.naming.tokenPadding < 1) {
    throw new Error("Token padding must be a positive integer.");
  }
  return config;
}

function validateWeights(label: string, options: readonly WeightedOption<unknown>[]): void {
  if (options.length === 0) throw new Error(`Collection ${label} options cannot be empty.`);
  for (const option of options) {
    if (!Number.isFinite(option.weight) || option.weight <= 0) {
      throw new Error(`Collection ${label} weights must be finite and positive.`);
    }
  }
}

function validateDefinitions(
  label: string,
  options: readonly WeightedOption<string>[],
  definitions: Readonly<Record<string, unknown>>,
): void {
  for (const option of options) {
    if (!definitions[option.value]) throw new Error(`Collection ${label} definition is missing: ${option.value}`);
  }
}

function validateResolution(label: string, resolution: number): void {
  if (!Number.isInteger(resolution) || resolution < 2 || (resolution & (resolution - 1)) !== 0) {
    throw new Error(`Collection ${label} texture resolution must be a power of two.`);
  }
}
